import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@kgc/ui';
import { Clock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ExecutionRow } from './components/ExecutionRow';
import { ExecuteWorkflowDialog } from './dialogs/ExecuteWorkflowDialog';
import type {
  FlowTab,
  PublishedWorkflow,
  WorkflowExecution,
  WorkflowExecutionStatus,
} from './types';

const PAGE_SIZE = 20;

type PeriodFilter = 'all' | 'today' | 'week' | 'month';

function isInPeriod(dateStr: string, period: PeriodFilter): boolean {
  if (period === 'all') return true;
  const date = new Date(dateStr);
  const now = new Date();
  if (period === 'today') {
    return date.toDateString() === now.toDateString();
  }
  if (period === 'week') {
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return date >= weekAgo;
  }
  if (period === 'month') {
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return date >= monthAgo;
  }
  return true;
}

interface FlowHistoryTabProps {
  executions: WorkflowExecution[];
  workflows: PublishedWorkflow[];
  onTabChange?: ((tab: FlowTab) => void) | undefined;
}

export function FlowHistoryTab({ executions, workflows, onTabChange }: FlowHistoryTabProps) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<WorkflowExecutionStatus | 'all'>('all');
  const [workflowFilter, setWorkflowFilter] = useState<string>('all');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [page, setPage] = useState(1);

  const [restartWorkflow, setRestartWorkflow] = useState<PublishedWorkflow | null>(null);
  const [restartParams, setRestartParams] = useState<Record<string, unknown>>({});

  const historyExecutions = useMemo(() => {
    let result = executions.filter(
      e => e.status === 'completed' || e.status === 'failed' || e.status === 'cancelled'
    );
    if (statusFilter !== 'all') {
      result = result.filter(e => e.status === statusFilter);
    }
    if (workflowFilter !== 'all') {
      result = result.filter(e => e.workflowId === workflowFilter);
    }
    if (periodFilter !== 'all') {
      result = result.filter(e => isInPeriod(e.completedAt ?? e.startedAt, periodFilter));
    }
    return result;
  }, [executions, statusFilter, workflowFilter, periodFilter]);

  const totalPages = Math.ceil(historyExecutions.length / PAGE_SIZE);
  const pagedExecutions = historyExecutions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const uniqueWorkflowIds = useMemo(() => {
    const ids = new Set(executions.map(e => e.workflowId));
    return [...ids];
  }, [executions]);

  function handleRestart(execution: WorkflowExecution) {
    const wf = workflows.find(w => w.id === execution.workflowId);
    if (!wf) return;
    setRestartWorkflow(wf);
    setRestartParams(execution.parameters);
  }

  function handleFilterChange() {
    setPage(1);
  }

  if (
    historyExecutions.length === 0 &&
    statusFilter === 'all' &&
    workflowFilter === 'all' &&
    periodFilter === 'all'
  ) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-muted-foreground"
        data-testid="flow-history-empty"
      >
        <Clock className="h-12 w-12 mb-3 opacity-50" aria-hidden="true" />
        <p className="text-sm mb-3">{t('integration.flow.history.noExecutions')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onTabChange?.('catalog')}
          data-testid="flow-history-start-cta"
        >
          {t('integration.flow.history.startOne')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select
          value={statusFilter}
          onValueChange={v => {
            setStatusFilter(v as WorkflowExecutionStatus | 'all');
            handleFilterChange();
          }}
        >
          <SelectTrigger className="w-40" data-testid="flow-history-filter-status">
            <SelectValue placeholder={t('integration.flow.history.filterStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('integration.flow.history.periodAll')}</SelectItem>
            <SelectItem value="completed">{t('integration.flow.status.completed')}</SelectItem>
            <SelectItem value="failed">{t('integration.flow.status.failed')}</SelectItem>
            <SelectItem value="cancelled">{t('integration.flow.status.cancelled')}</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={workflowFilter}
          onValueChange={v => {
            setWorkflowFilter(v);
            handleFilterChange();
          }}
        >
          <SelectTrigger className="w-44" data-testid="flow-history-filter-workflow">
            <SelectValue placeholder={t('integration.flow.history.filterWorkflow')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('integration.flow.history.periodAll')}</SelectItem>
            {uniqueWorkflowIds.map(id => {
              const wf = workflows.find(w => w.id === id);
              return (
                <SelectItem key={id} value={id}>
                  {wf?.name ?? id}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <Select
          value={periodFilter}
          onValueChange={v => {
            setPeriodFilter(v as PeriodFilter);
            handleFilterChange();
          }}
        >
          <SelectTrigger className="w-44" data-testid="flow-history-filter-period">
            <SelectValue placeholder={t('integration.flow.history.filterPeriod')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('integration.flow.history.periodAll')}</SelectItem>
            <SelectItem value="today">{t('integration.flow.history.periodToday')}</SelectItem>
            <SelectItem value="week">{t('integration.flow.history.periodWeek')}</SelectItem>
            <SelectItem value="month">{t('integration.flow.history.periodMonth')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Execution list */}
      {pagedExecutions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <p className="text-sm">{t('integration.flow.history.noResults')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pagedExecutions.map(exec => (
            <div key={exec.id} className="relative">
              <ExecutionRow execution={exec} />
              <div className="absolute top-3 right-12">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRestart(exec)}
                  data-testid={`flow-restart-btn-${exec.id}`}
                  className="text-xs"
                >
                  {t('integration.flow.history.restart')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-center gap-2"
          data-testid="flow-history-pagination"
        >
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            ←
          </Button>
          <span className="text-sm text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            →
          </Button>
        </div>
      )}

      {/* Restart dialog */}
      <ExecuteWorkflowDialog
        workflow={restartWorkflow}
        open={restartWorkflow !== null}
        onOpenChange={open => {
          if (!open) {
            setRestartWorkflow(null);
            setRestartParams({});
          }
        }}
        onSuccess={() => {
          setRestartWorkflow(null);
          setRestartParams({});
          onTabChange?.('running');
        }}
        initialParameters={restartParams}
      />
    </div>
  );
}
